import { Car, Mail, Phone } from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';

import { clsx } from 'clsx';

import { useCdekWidget } from '@screens/PlacingOrder/ui/CdekWidget/CdekWidget';
import { LightBoxModal } from '@widgets/LightBoxModal';

import cls from './PointInfoContent.module.scss';

const IMAGES_LENGTH = 3;

export const PointInfoContent = memo(() => {
  const {
    selectedPoint: {
      location: { city, address, postal_code } = {},
      work_time,
      nearest_station,
      nearest_metro_station,
      note,
      office_image_list = [],
      have_cash,
      have_cashless,
      have_fast_payment_system,
      email,
      phones
    } = {}
  } = useCdekWidget() || {};

  const [openLightBoxModal, setOpenLightBoxModal] = useState(false);
  const [currentImage, setCurrentImage] = useState('');

  const handleOpenLightboxModal = useCallback(
    (url?: string) => () => {
      setOpenLightBoxModal((prevState) => !prevState);
      if (url) {
        setCurrentImage(url);
      }
    },
    [setOpenLightBoxModal]
  );

  const images = useMemo(() => office_image_list.map(({ url }) => url), [office_image_list]);

  return (
    <div className={cls.wrapper}>
      <div className={cls.item}>
        <div className={cls.title}>{`${city}, ${address}`}</div>
      </div>
      <div className={cls.item}>
        <div className={cls.title}>Время работы</div>
        <div className={cls.description}>{work_time}</div>
      </div>
      {nearest_station && (
        <div className={cls.item}>
          <div className={cls.title}>Ближайшая остановка</div>
          <div className={cls.description}>{nearest_station}</div>
        </div>
      )}
      {nearest_metro_station && (
        <div className={cls.item}>
          <div className={cls.title}>Ближайшая станция метро</div>
          <div className={cls.description}>{nearest_metro_station}</div>
        </div>
      )}
      <div className={cls.item}>
        <div className={cls.title}>Способы оплаты</div>
        <div className={cls.description}>
          <ul>
            {have_cash && <li>Наличными</li>}
            {have_cashless && <li>Банковской картой</li>}
            {have_fast_payment_system && <li>QR-кодом через СБП</li>}
          </ul>
        </div>
      </div>
      {note && (
        <div className={cls.item}>
          <div className={cls.title}>Как добраться</div>
          <div className={cls.description}>{note}</div>
          {images.length && (
            <div className={cls.description}>
              <div className={cls.imagesWrapper}>
                {images.slice(0, IMAGES_LENGTH).map((url, index) => {
                  const moreImages = images.length > IMAGES_LENGTH;

                  return (
                    <div
                      key={url}
                      className={cls.image}
                      style={{
                        backgroundImage: `url(${url})`
                      }}
                    >
                      <div
                        className={clsx(cls.imageHighlight, {
                          [cls.moreImages]: moreImages && index === 2
                        })}
                        onClick={handleOpenLightboxModal(url)}
                      >
                        {moreImages && index === 2 ? `+${images.length - IMAGES_LENGTH}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
      <div className={cls.item}>
        <div className={cls.title}>Контакты</div>
        {email && (
          <div className={cls.description}>
            <Mail />
            <div>{email}</div>
          </div>
        )}
        {phones &&
          phones.map(({ number }) => (
            <div
              key={number}
              className={cls.description}
            >
              <Phone />
              <div>{number}</div>
            </div>
          ))}
        {postal_code && (
          <div className={cls.description}>
            <Car />
            <div>{postal_code}</div>
          </div>
        )}
      </div>
      {currentImage && openLightBoxModal && (
        <LightBoxModal
          open={openLightBoxModal}
          onClose={handleOpenLightboxModal()}
          currentImageUrl={currentImage}
          imageUrls={images}
        />
      )}
    </div>
  );
});
